/**
 * Nome visível na chamada Twilio Video.
 * Identity de agente autenticado é `agent_{userId}_{roomId}` — não é o nome da pessoa.
 */

export function isPlaceholderVideoLabel(name) {
  const n = String(name || "").trim();
  if (!n) return true;
  if (/^(participante|convidado|visitante|atendente|você|voce)$/i.test(n)) return true;
  if (/^agente?\s*\d+(\s+\d+)*$/i.test(n)) return true;
  if (/^[\d\s]+$/.test(n)) return true;
  return false;
}

/** SID de participante Twilio (ex.: PA… usado em eventos de transcription). */
export function looksLikeTwilioParticipantSid(value) {
  return /^PA[a-f0-9]{32}$/i.test(String(value || "").trim());
}

export function participantLabelFromIdentity(identity, knownName, fallback = "Participante") {
  const known = String(knownName || "").trim();
  if (known && !isPlaceholderVideoLabel(known) && !looksLikeTwilioParticipantSid(known)) {
    return known.slice(0, 80);
  }

  const raw = String(identity || "").trim();
  if (!raw) return fallback;
  if (looksLikeTwilioParticipantSid(raw)) return fallback;

  const role = /^agent_/i.test(raw) ? "agent" : /^guest_/i.test(raw) ? "guest" : null;
  let name = raw.replace(/^(guest_|agent_)/i, "");
  name = name.replace(/_[a-f0-9]{4,8}$/i, "");
  name = name.replace(/_/g, " ").trim();

  if (role === "agent") {
    if (!name || /^[\d\s]+$/.test(name) || isPlaceholderVideoLabel(name)) {
      return known && !isPlaceholderVideoLabel(known) ? known : "Atendente";
    }
    return name;
  }

  return name || fallback;
}

function findParticipantBySid(room, sid) {
  const target = String(sid || "").trim();
  if (!target || !room) return null;
  if (room.localParticipant?.sid === target) return room.localParticipant;
  const fromMap = room.participants?.get?.(target);
  if (fromMap) return fromMap;
  let found = null;
  room.participants?.forEach?.((p) => {
    if (!found && p?.sid === target) found = p;
  });
  return found;
}

/**
 * Resolve o rótulo do falante nas legendas (nunca exibe SID PA…).
 */
export function resolveCaptionSpeakerLabel({
  identity = null,
  participantSid = null,
  participant = null,
  displayName = null,
  room = null,
  labelForIdentity = null,
  fallback = "Participante",
} = {}) {
  const named = String(displayName || "").trim();
  if (named && !looksLikeTwilioParticipantSid(named) && !isPlaceholderVideoLabel(named)) {
    return named.slice(0, 80);
  }

  const resolveIdentity = (id) => {
    const raw = String(id || "").trim();
    if (!raw || looksLikeTwilioParticipantSid(raw)) return null;
    if (typeof labelForIdentity === "function") {
      const label = String(labelForIdentity(raw) || "").trim();
      if (label && !looksLikeTwilioParticipantSid(label)) return label.slice(0, 80);
    }
    const label = participantLabelFromIdentity(raw);
    if (label && !looksLikeTwilioParticipantSid(label)) return label.slice(0, 80);
    return null;
  };

  const fromIdentity = resolveIdentity(identity);
  if (fromIdentity) return fromIdentity;

  let sid = String(participantSid || "").trim();
  if (!sid && typeof participant === "string") sid = participant.trim();
  if (!sid && participant && typeof participant === "object") {
    sid = String(participant.sid || "").trim();
    const fromObjIdentity = resolveIdentity(participant.identity);
    if (fromObjIdentity) return fromObjIdentity;
  }
  if (!sid && looksLikeTwilioParticipantSid(identity)) {
    sid = String(identity).trim();
  }

  if (sid) {
    const p =
      (participant && typeof participant === "object" && participant.sid === sid
        ? participant
        : null) || findParticipantBySid(room, sid);
    const fromSidIdentity = resolveIdentity(p?.identity);
    if (fromSidIdentity) return fromSidIdentity;
  }

  return fallback;
}
